import { Button, Card, CardBody, CardHeader, Chip, Input, Spinner } from '@nextui-org/react'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { IconCrossFilled } from '~/components/icons'
import {
  addAllowedEmailDomain,
  getAllowedEmailDomains,
  removeAllowedEmailDomain
} from '~/services/superAdminService'
import { BetterToast } from '~/utils/toast'

export default function AllowedEmailDomains() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [domains, setDomains] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAdding, setIsAdding] = useState(false)
  const [removingDomain, setRemovingDomain] = useState<string | null>(null)
  const [newDomain, setNewDomain] = useState('')
  const [searchFilter, setSearchFilter] = useState(() => searchParams.get('search') || '')

  // Sync searchFilter to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    if (searchFilter) params.set('search', searchFilter)
    else params.delete('search')
    setSearchParams(params, { replace: true })
  }, [searchFilter, setSearchParams, searchParams])

  const fetchDomains = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await getAllowedEmailDomains()
      setDomains(response.domains)
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to fetch allowed domains')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDomains()
  }, [fetchDomains])

  const handleAddDomain = async () => {
    const domain = newDomain.trim().toLowerCase()
    if (!domain) {
      BetterToast.error('Please enter a domain')
      return
    }

    // Basic domain validation
    if (!domain.includes('.') || domain.includes('@')) {
      BetterToast.error('Please enter a valid domain (e.g., gmail.com)')
      return
    }

    try {
      setIsAdding(true)
      const response = await addAllowedEmailDomain(domain)
      setDomains(response.domains)
      setNewDomain('')
      BetterToast.success(response.message || `Added ${domain} to allowed list`)
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to add domain')
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemoveDomain = async (domain: string) => {
    try {
      setRemovingDomain(domain)
      const response = await removeAllowedEmailDomain(domain)
      setDomains(response.domains)
      BetterToast.success(response.message || `Removed ${domain} from allowed list`)
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to remove domain')
    } finally {
      setRemovingDomain(null)
    }
  }

  const filteredDomains = domains.filter(domain =>
    domain.toLowerCase().includes(searchFilter.toLowerCase())
  )

  return (
    <Card radius='sm' shadow='sm' className='mt-4'>
      <CardHeader className='p-4 flex flex-col items-start gap-1'>
        <h2 className='text-lg font-semibold'>Allowed Email Domains (Allowlist)</h2>
        <p className='text-sm text-gray-500'>
          Manage trusted email domains that skip external API validation during signup. These
          domains are trusted providers (e.g., gmail.com, outlook.com) that don&apos;t need
          additional verification.
        </p>
      </CardHeader>
      <CardBody className='p-4 flex flex-col gap-4'>
        {/* Add new domain */}
        <div className='flex gap-2 items-end'>
          <Input
            label='Add new domain'
            placeholder='e.g., company.com'
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddDomain()}
            className='max-w-xs'
            size='sm'
            isDisabled={isAdding}
          />
          <Button
            color='success'
            size='md'
            onClick={handleAddDomain}
            isLoading={isAdding}
            isDisabled={!newDomain.trim()}
          >
            Add Domain
          </Button>
        </div>

        {/* Search filter */}
        <div className='flex gap-2 items-center'>
          <Input
            placeholder='Search domains...'
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            className='max-w-xs'
            size='sm'
            isClearable
            onClear={() => setSearchFilter('')}
          />
          <span className='text-sm text-gray-500'>
            {filteredDomains.length} of {domains.length} domains
          </span>
        </div>

        {/* Domain list */}
        <div className='border rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto bg-gray-50'>
          {isLoading ? (
            <div className='flex justify-center items-center h-[200px]'>
              <Spinner size='lg' />
            </div>
          ) : filteredDomains.length === 0 ? (
            <div className='flex justify-center items-center h-[200px] text-gray-500'>
              {domains.length === 0
                ? 'No allowed domains configured'
                : 'No domains match your search'}
            </div>
          ) : (
            <div className='flex flex-wrap gap-2'>
              {filteredDomains.map(domain => (
                <Chip
                  key={domain}
                  variant='flat'
                  color='success'
                  onClose={() => handleRemoveDomain(domain)}
                  isDisabled={removingDomain === domain}
                  classNames={{
                    base: 'h-8',
                    content: 'font-mono text-sm'
                  }}
                  endContent={
                    removingDomain === domain ? (
                      <Spinner size='sm' className='ml-1' />
                    ) : (
                      <IconCrossFilled className='w-3 h-3 ml-1 cursor-pointer hover:opacity-70' />
                    )
                  }
                >
                  {domain}
                </Chip>
              ))}
            </div>
          )}
        </div>

        {/* Info text */}
        <p className='text-xs text-gray-400'>
          Note: Emails from allowed domains skip external API validation, making signup faster. This
          is useful for known legitimate email providers and corporate domains.
        </p>
      </CardBody>
    </Card>
  )
}
